<script setup>
import { computed, onUnmounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import API from 'dashboard/api/my_ai/knowledgeBase';
import PageLayout from 'dashboard/components-next/my_ai/PageLayout.vue';
import RecordReview from './RecordReview.vue';
import DraftEditor from './DraftEditor.vue';
import { labels as messages } from './labels';

const route = useRoute();
const { locale } = useI18n();
const labels = computed(
  () => messages[locale.value?.startsWith('ar') ? 'ar' : 'en']
);
const assistantId = computed(() => Number(route.params.assistantId));
const summary = ref(null);
const records = ref([]);
const proposals = ref([]);
const total = ref(0);
const page = ref(1);
const tab = ref('records');
const query = ref('');
const status = ref('');
const selected = ref(null);
const editing = ref(null);
const proposalId = ref(null);
const busy = ref(false);
const loading = ref(false);
const error = ref('');
const testQuery = ref('');
const preview = ref(true);
const results = ref(null);
const answer = ref(null);
const history = ref([]);
let controller;
let generation = 0;

const showError = exception => {
  error.value =
    exception.response?.data?.error || exception.message || labels.value.error;
};
const createFaqDraft = () => {
  editing.value = {
    record_id: `TH-${summary.value.entity}-FAQ-${crypto.randomUUID()}`,
    entity: summary.value.entity,
    title: '',
    question: '',
    answer: '',
    record_type: 'faq',
    topic: 'customer_support',
    language: locale.value?.startsWith('ar') ? 'ar' : 'en',
    search_aliases: [],
    conditions: [],
    exceptions: [],
    applicability: {
      account_types: [],
      customer_categories: [],
      other_restrictions: [],
    },
    source_references: [],
    review_notes: '',
  };
};
const load = async () => {
  controller?.abort();
  controller = new AbortController();
  const id = assistantId.value;
  generation += 1;
  const current = generation;
  loading.value = true;
  error.value = '';
  try {
    const [base, list, queue] = await Promise.all([
      API.summary(id, controller.signal),
      API.records(
        id,
        { page: page.value, query: query.value, status: status.value },
        controller.signal
      ),
      API.proposals(id, controller.signal),
    ]);
    if (current !== generation) return;
    summary.value = base.data;
    records.value = list.data.records;
    total.value = list.data.total;
    proposals.value = queue.data;
    const requestedRecord = route.query.record;
    if (requestedRecord) {
      selected.value =
        records.value.find(item => item.record.record_id === requestedRecord) ||
        null;
    }
    if (route.query.create === 'faq' && !editing.value && !selected.value) {
      createFaqDraft();
    }
  } catch (exception) {
    if (current === generation && exception.code !== 'ERR_CANCELED')
      showError(exception);
  } finally {
    if (current === generation) loading.value = false;
  }
};
watch(
  assistantId,
  () => {
    page.value = 1;
    selected.value = null;
    editing.value = null;
    proposalId.value = null;
    summary.value = null;
    records.value = [];
    proposals.value = [];
    results.value = null;
    answer.value = null;
    history.value = [];
    busy.value = false;
    query.value = route.query.record || '';
    status.value = '';
    load();
  },
  { immediate: true }
);
onUnmounted(() => {
  generation += 1;
  controller?.abort();
});

const mutate = async (action, data) => {
  const id = assistantId.value;
  busy.value = true;
  error.value = '';
  try {
    const response = await API.action(id, action, data);
    if (id !== assistantId.value) return null;
    await load();
    if (id !== assistantId.value) return null;
    return response.data;
  } catch (exception) {
    if (id === assistantId.value) showError(exception);
    return null;
  } finally {
    if (id === assistantId.value) busy.value = false;
  }
};
const review = async (action, note) => {
  const data = await mutate(action, {
    record_id: selected.value.id,
    content_sha256: selected.value.content_sha256,
    note,
  });
  if (data) selected.value = data;
};
const lines = value =>
  value
    .split('\n')
    .map(item => item.trim())
    .filter(Boolean);
const saveDraft = async form => {
  const payload = {
    ...editing.value,
    title: form.title,
    question: form.question,
    answer: form.answer,
    record_type: form.record_type,
    conditions: lines(form.conditions),
    exceptions: lines(form.exceptions),
    effective_from: form.effective_from || null,
    valid_through: form.valid_through || null,
    approval_status: 'pending_review',
    verification_status: form.verified ? 'source_confirmed' : 'not_found',
    handling: form.verified ? 'answer_candidate' : 'human_review',
  };
  const sourceId = `AE-REVIEW-${payload.record_id}`;
  payload.source_references = [
    {
      source_id: sourceId,
      url_or_file: form.sourceUrl,
      location: form.sourceLocation,
      supporting_excerpt: form.sourceExcerpt,
    },
  ];
  const sources = [
    {
      source_id: sourceId,
      url_or_file: form.sourceUrl,
      title: form.title,
      source_type: 'official_website',
      access_status: form.verified ? 'inspected_by_admin' : 'unverified',
    },
  ];
  const data = await mutate(
    proposalId.value ? 'incorporate_proposal' : 'revise',
    { record: payload, sources, note: form.note, proposal_id: proposalId.value }
  );
  if (data) {
    selected.value = data;
    editing.value = null;
    proposalId.value = null;
    tab.value = 'records';
  }
};
const editProposal = proposal => {
  proposalId.value = proposal.id;
  editing.value = {
    record_id: `TH-${summary.value.entity}-L${proposal.id}`,
    entity: summary.value.entity,
    title: proposal.question,
    question: proposal.question,
    answer: proposal.proposed_answer,
    record_type: 'faq',
    topic: 'customer_support',
    language: 'en',
    search_aliases: [],
    conditions: [],
    exceptions: [],
    applicability: {
      account_types: [],
      customer_categories: [],
      other_restrictions: [],
    },
    source_references: [],
    review_notes: '',
    researched_at: new Date().toISOString().slice(0, 10),
  };
};
const dismiss = async proposal => {
  const note = window.prompt(labels.value.note);
  if (note?.trim())
    await mutate('dismiss_proposal', { proposal_id: proposal.id, note });
};
const search = async generateAnswer => {
  if (!testQuery.value.trim()) return;
  busy.value = true;
  error.value = '';
  answer.value = null;
  const id = assistantId.value;
  try {
    let action = preview.value ? 'preview' : 'search';
    if (generateAnswer) action = 'preview_answer';
    const response = await API.action(id, action, { query: testQuery.value });
    if (id !== assistantId.value) return;
    if (generateAnswer) answer.value = response.data;
    else results.value = response.data;
  } catch (exception) {
    if (id === assistantId.value) showError(exception);
  } finally {
    if (id === assistantId.value) busy.value = false;
  }
};
const showHistory = async () => {
  const id = assistantId.value;
  const recordId = selected.value.id;
  try {
    const response = await API.history(id, recordId);
    if (id === assistantId.value && recordId === selected.value?.id)
      history.value = response.data;
  } catch (exception) {
    if (id === assistantId.value) showError(exception);
  }
};
const changePage = amount => {
  page.value += amount;
  load();
};
</script>

<template>
  <PageLayout
    :header-title="labels.title"
    :show-pagination-footer="false"
    :show-know-more="false"
  >
    <template #headerActions>
      <button
        type="button"
        class="px-3 py-2 text-sm border rounded-lg border-n-weak"
        @click="load"
      >
        {{ labels.refresh }}
      </button>
    </template>
    <template #body>
      <div class="max-w-7xl pb-8 mx-auto">
        <p
          v-if="error"
          role="alert"
          class="p-4 my-4 text-sm border rounded-lg text-n-ruby-11 border-n-ruby-6"
        >
          {{ error }}
        </p>
        <p v-if="loading" class="my-4 text-sm text-n-slate-11">
          {{ labels.loading }}
        </p>
        <div v-if="summary" class="grid grid-cols-2 gap-4 my-5 lg:grid-cols-4">
          <div
            v-for="metric in [
              { key: 'records', value: summary.records },
              { key: 'approved', value: summary.approved },
              { key: 'indexed', value: summary.indexed },
              { key: 'pendingProposals', value: summary.pending_proposals },
            ]"
            :key="metric.key"
            class="p-4 border rounded-xl bg-n-solid-1 border-n-weak"
          >
            <p class="text-sm text-n-slate-11">
              {{ summary.entity }} · {{ labels[metric.key] }}
            </p>
            <p class="mt-1 text-2xl font-semibold tabular-nums">
              {{ metric.value }}
            </p>
          </div>
        </div>
        <nav class="flex gap-2 mb-5" :aria-label="labels.title">
          <button
            v-for="item in ['records', 'search', 'learning']"
            :key="item"
            type="button"
            class="px-4 py-2 text-sm rounded-lg"
            :class="tab === item ? 'bg-n-brand text-white' : 'bg-n-alpha-2'"
            @click="tab = item"
          >
            {{ labels[item] }}
          </button>
        </nav>
        <div v-if="editing" class="max-w-3xl mx-auto">
          <DraftEditor
            :key="editing.record_id"
            :record="editing"
            :labels="labels"
            :busy="busy"
            @save="saveDraft"
            @cancel="
              editing = null;
              proposalId = null;
            "
          />
        </div>
        <div
          v-else-if="tab === 'records'"
          class="grid gap-5 lg:grid-cols-[320px_1fr]"
        >
          <section class="flex flex-col gap-3">
            <form
              class="flex flex-col gap-3"
              @submit.prevent="
                page = 1;
                load();
              "
            >
              <input
                v-model="query"
                :aria-label="labels.filter"
                :placeholder="labels.filter"
                class="p-3 text-sm border rounded-lg bg-n-solid-1 border-n-weak"
              /><select
                v-model="status"
                :aria-label="labels.all"
                class="p-3 text-sm border rounded-lg bg-n-solid-1 border-n-weak"
                @change="
                  page = 1;
                  load();
                "
              >
                <option value="">{{ labels.all }}</option>
                <option
                  v-for="item in ['pending_review', 'approved', 'withdrawn']"
                  :key="item"
                  :value="item"
                >
                  {{ labels[item] }}
                </option>
              </select>
            </form>
            <button
              v-for="record in records"
              :key="record.id"
              type="button"
              class="p-4 text-start border rounded-lg bg-n-solid-1"
              :class="
                selected?.id === record.id ? 'border-n-blue-8' : 'border-n-weak'
              "
              @click="
                selected = record;
                history = [];
              "
            >
              <span class="block text-xs text-n-slate-11"
                >{{ record.record.record_id }} ·
                {{ labels[record.approval_status] }}</span
              ><span class="block mt-1 text-sm font-medium">{{
                record.record.title
              }}</span>
            </button>
            <p
              v-if="!records.length && !loading"
              class="p-4 text-sm text-n-slate-11"
            >
              {{ labels.empty }}
            </p>
            <div class="flex items-center justify-between text-sm">
              <button
                type="button"
                :disabled="page === 1"
                class="disabled:opacity-40"
                @click="changePage(-1)"
              >
                {{ labels.previous }}</button
              ><span>{{ page }} / {{ Math.max(1, Math.ceil(total / 50)) }}</span
              ><button
                type="button"
                :disabled="page * 50 >= total"
                class="disabled:opacity-40"
                @click="changePage(1)"
              >
                {{ labels.next }}
              </button>
            </div>
          </section>
          <section>
            <RecordReview
              v-if="selected"
              :record="selected"
              :labels="labels"
              :busy="busy"
              @approve="review('approve', $event)"
              @withdraw="review('withdraw', $event)"
              @revise="editing = JSON.parse(JSON.stringify(selected.record))"
              @history="showHistory"
            />
            <p
              v-else
              class="p-8 text-sm border rounded-xl border-n-weak text-n-slate-11"
            >
              {{ labels.choose }}
            </p>
            <div
              v-if="history.length"
              class="p-4 mt-5 border rounded-xl border-n-weak"
            >
              <h3 class="font-medium">{{ labels.history }}</h3>
              <div
                v-for="revision in history"
                :key="revision.id"
                class="p-3 mt-2 border rounded-lg border-n-weak"
              >
                <p class="text-xs text-n-slate-11">
                  {{ revision.revision }} ·
                  {{ labels[revision.approval_status] }}
                </p>
                <p class="mt-2 text-sm whitespace-pre-wrap">
                  {{ revision.record.answer }}
                </p>
              </div>
            </div>
          </section>
        </div>
        <section v-else-if="tab === 'search'" class="max-w-3xl">
          <form class="flex flex-col gap-4" @submit.prevent="search(false)">
            <input
              v-model="testQuery"
              :aria-label="labels.query"
              :placeholder="labels.query"
              class="p-3 border rounded-lg bg-n-solid-1 border-n-weak"
            /><label class="flex gap-2 text-sm"
              ><input v-model="preview" type="checkbox" />{{
                labels.preview
              }}</label
            >
            <div class="flex gap-2">
              <button
                type="submit"
                class="px-4 py-2 rounded-lg bg-n-brand text-white"
                :disabled="busy"
              >
                {{ labels.test }}</button
              ><button
                type="button"
                class="px-4 py-2 border rounded-lg border-n-weak"
                :disabled="busy"
                @click="search(true)"
              >
                {{ labels.generate }}
              </button>
            </div>
          </form>
          <p
            v-if="results?.preview_only || answer?.preview_only"
            class="p-3 my-4 text-sm rounded-lg bg-n-amber-2 text-n-amber-11"
          >
            {{ labels.previewWarning }}
          </p>
          <p
            v-if="answer"
            class="p-4 my-4 whitespace-pre-wrap border rounded-xl border-n-weak"
          >
            {{ answer.response }}
          </p>
          <p v-if="results && !results.records.length" class="p-4 text-sm">
            {{ labels.noAnswer }}
          </p>
          <article
            v-for="record in results?.records || []"
            :key="record.record_id"
            class="p-4 my-4 border rounded-xl bg-n-solid-1 border-n-weak"
          >
            <p class="text-xs text-n-slate-11">
              {{ record.record_id }} · {{ record.revision }}
            </p>
            <h3 class="mt-2 font-medium">{{ record.title }}</h3>
            <p class="mt-2 text-sm whitespace-pre-wrap">{{ record.answer }}</p>
          </article>
        </section>
        <section v-else class="max-w-3xl">
          <p class="p-4 mb-4 text-sm rounded-lg bg-n-amber-2 text-n-amber-11">
            {{ labels.proposalWarning }}
          </p>
          <p v-if="!proposals.length" class="p-4 text-sm text-n-slate-11">
            {{ labels.noProposals }}
          </p>
          <article
            v-for="proposal in proposals"
            :key="proposal.id"
            class="p-5 mb-4 border rounded-xl bg-n-solid-1 border-n-weak"
          >
            <p class="text-xs text-n-slate-11">
              {{ labels[proposal.kind] }} · {{ proposal.occurrences }}
            </p>
            <h3 class="mt-2 font-medium">{{ proposal.question }}</h3>
            <p class="mt-3 text-sm whitespace-pre-wrap">
              {{ proposal.proposed_answer }}
            </p>
            <div class="flex gap-2 mt-4">
              <button
                type="button"
                class="px-3 py-2 text-sm rounded-lg bg-n-brand text-white"
                @click="editProposal(proposal)"
              >
                {{ labels.proposal }}</button
              ><button
                type="button"
                class="px-3 py-2 text-sm border rounded-lg border-n-weak"
                @click="dismiss(proposal)"
              >
                {{ labels.dismiss }}
              </button>
            </div>
          </article>
        </section>
      </div>
    </template>
  </PageLayout>
</template>
